// R109 — **WHAT STANDS WHERE THE SHELF WOULD HAVE STOOD, AND THE ORDER OF THE ARMS WAS THE DEFECT.**
//
// The Library's empty state is a FIVE-WAY decision — did the catalogue fail, is it still coming, did
// she filter it away, is this shelf genuinely bare — and it lived as a five-arm ternary chain inside
// `LibraryPage`, a component holding twenty hooks. R103's `placeNote` and R64's Cauldron panel are
// the two precedents on this line and both found a live defect the moment the decision became
// callable. This one found two, and one of them is a sentence T8-9 and R3 have each already killed
// once on the other half of this very block.
//
// **(1) THE VIDEO ARM SAT ABOVE BOTH SEARCH ARMS.** The search box is ONE input serving both tabs and
// `shelfBooks` filters by it for both media, so a member on the Video tab who types anything no video
// is titled read
//
//     "The Video Library is ready. Class videos will appear here on their own shelves once they're
//      added."
//
// over a video library that is fully stocked and merely filtered. **That is R3's finding one media
// over** — *"the shelf is filtered, not empty, and the difference is the whole sentence"* — landing on
// **T8-9's own forbidden shape**, the app stating as fact something it has no way of knowing. R3
// wrote the two search arms and put them BELOW the media arm, so the fix it shipped could only ever
// reach books.
//
// **(2) `loading` IS THE FOURTH VALUE OF `shelfLoad` AND THE CHAIN NEVER READ IT.** R94 gave the
// shelf a four-state load; the empty sentence branched on `error` and treated the other three as one
// thing. On the BOOK tab that is invisible, because the in-code catalogue draws while the read is in
// flight and the block is never reached. **On the VIDEO tab — which is the Library's default landing
// view — `src/catalogs/sacred-text-books.ts` holds ZERO videos**, so `shelfSource` returns 46 books,
// the media filter takes them all away, and **every single load of the Library opens on the sentence
// above until the fetch lands.** Still looking is not empty (R64's four-way pot, W5-2/R39's third
// state), and the conservative answer is to draw NOTHING rather than to invent a sentence she never
// asked for — the books have never drawn one either.
//
// **(3) AND THE COUNT NOTE ABOVE THE SHELF SAYS "books" ON BOTH TABS.** That one is reachable today
// and always has been: a search on the Video tab prints *"0 books titled 'x'"* over a wall of videos.
// It is the same decision — which noun does this media wear — so it is answered here, once, rather
// than left as the half that stayed wrong after the empty state was fixed.
//
// **THE KEY IS RETURNED, NEVER THE WORDS** (R103): this module imports NOTHING, which is the entry
// requirement that lets a `.mjs` gate load and RUN it, and 14-B3's seam says the catalog is the one
// place a sentence lives. **Every key is a LITERAL** — 14-B7d's lesson, where four assembled keys
// went invisible to the catalog's own "is this row read by anything" law all at once.
//
// **THE ORDER IS THE LAW AND IT IS HELD BY POSITION** (R52), so no later spelling can put the media
// arm back in front of the search arms:
//
//     error  ->  loading  ->  she filtered it  ->  this media is bare
//
// A shelf that did not load knows nothing about her search; a shelf that has not arrived knows
// nothing about anything.

export type LibraryShelfLoad = "loading" | "db" | "fallback" | "error";
export type LibraryMediaKind = "book" | "video";

// `null` is a real answer and means DRAW NOTHING. It is not the absence of a decision.
export type LibraryNote = { key: string; vars?: Record<string, string | number> } | null;

export function libraryEmptyNote(state: {
  load: LibraryShelfLoad;
  media: LibraryMediaKind;
  /** Her typed words, already trimmed. Empty means she is not searching. */
  term: string;
  /** How many passages the in-book text search found. Books only; a video has no pages. */
  passages: number;
}): LibraryNote {
  // R94: we could not ask. This is the one arm that outranks everything, because a catalogue that
  // never arrived cannot tell you whether her search matched.
  if (state.load === "error") return { key: "library.empty.error" };
  // R64/R39's third state. Nothing is drawn: a blank moment is honest, a sentence is not.
  if (state.load === "loading") return null;
  if (state.term) {
    // R3's ruling, now reaching both media: the shelf is FILTERED, not empty.
    if (state.media === "video") return { key: "library.empty.videoTitled", vars: { term: state.term } };
    // The passages sit directly above this line, so the sentence must not call the search a failure
    // while twenty-five real hits are on screen (R3).
    if (state.passages > 0) return { key: "library.empty.titledWithPassages", vars: { term: state.term } };
    return { key: "library.empty.titled", vars: { term: state.term } };
  }
  if (state.media === "video") return { key: "library.empty.video" };
  return { key: "library.empty.none" };
}

// WHICH NOUN THE COUNT ABOVE THE SHELF WEARS. Same decision, one control up — and the half that has
// been visibly wrong on the Video tab since the toggle shipped.
export function librarySearchCount(media: LibraryMediaKind, count: number, term: string): LibraryNote {
  if (!term) return null;
  const vars = { count, term };
  if (media === "video") return { key: count === 1 ? "library.titled.videoOne" : "library.titled.videoMany", vars };
  return { key: count === 1 ? "library.titled.one" : "library.titled.many", vars };
}
